import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';

/* =========================================================
   Types — extended to include referral + signup_bonus + transfer
========================================================= */

export type CreditType =
  | 'deposit'
  | 'referral'
  | 'signup_bonus'
  | 'game_win'
  | 'transfer';

export type CreditSource =
  | 'verify.et'
  | 'referral'
  | 'signup'
  | 'game'
  | 'transfer'
  | 'system';

export type CreditStatus = 'pending' | 'completed' | 'failed';

export interface CreditStats {
  totalCredits: number;
  todayCredits: number;
  verifyCredits: number;
  todayVerifyCredits: number;
  adminCredits: number;
  todayAdminCredits: number;
  totalTransactions: number;
}

export interface CreditListItem {
  id: string;
  userId: string | null;
  telegramId: string;
  userName: string;
  type: CreditType;
  source: CreditSource;
  amount: number;
  currency: string;
  balanceAfter: number;
  status: CreditStatus;
  referenceNumber: string;
  bank: string;
  senderName: string;
  receiverName: string;
  note: string;
  createdAt: string;
}

export interface CreditDetail extends CreditListItem {
  userUsername: string;
  userPhone: string;
  userBalance: number;
  accountSuffix: string;
  receiverAccount: string;
  requestId: string;
  verificationPayload: any;
  updatedAt: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface CreditsResponse {
  credits: CreditListItem[];
  pagination: Pagination;
}

export interface GetCreditsParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: 'all' | CreditType;
  source?: 'all' | CreditSource;
  status?: 'all' | CreditStatus;
  receiverName?: string;
}

export interface UserSearchResult {
  id: string;
  telegramId: string;
  fullName: string;
  username: string;
  phoneNumber: string;
  balance: number;
}

export interface CreateAdminCreditInput {
  userId: string;
  amount: number;
  note?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

class CreditService {
  async getStats(): Promise<CreditStats> {
    const res = await apiClient.get<ApiEnvelope<CreditStats>>(
      API_ENDPOINTS.CREDITS.STATS
    );
    return res.data.data;
  }

  async getAll(params: GetCreditsParams = {}): Promise<CreditsResponse> {
    const res = await apiClient.get<ApiEnvelope<CreditsResponse>>(
      API_ENDPOINTS.CREDITS.GET_ALL,
      { params }
    );
    return res.data.data;
  }

  async getOne(id: string): Promise<CreditDetail> {
    const res = await apiClient.get<ApiEnvelope<CreditDetail>>(
      API_ENDPOINTS.CREDITS.GET_ONE(id)
    );
    return res.data.data;
  }

  async create(input: CreateAdminCreditInput): Promise<{ id: string }> {
    const res = await apiClient.post<ApiEnvelope<{ id: string }>>(
      API_ENDPOINTS.CREDITS.CREATE,
      input
    );
    return res.data.data;
  }

  async searchUsers(search: string): Promise<UserSearchResult[]> {
    const res = await apiClient.get<ApiEnvelope<UserSearchResult[]>>(
      API_ENDPOINTS.CREDITS.SEARCH_USERS,
      { params: { search } }
    );
    return res.data.data;
  }

  /** Fetches distinct receiver names for the filter dropdown. */
  async getReceiverNames(): Promise<string[]> {
    const res = await apiClient.get<ApiEnvelope<string[]>>(
      API_ENDPOINTS.CREDITS.RECEIVER_NAMES
    );
    return res.data.data;
  }
}

const creditService = new CreditService();
export default creditService;